import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Typography, Box } from "@mui/material"
import CountUp from "react-countup"

import OverviewCard, {
  overviewCardValueSx,
  overviewCardHelperTextSx,
} from "./OverviewCard"
import { overviewCardsGridSx4 } from "./constants/overviewGrid"
import { OVERVIEW_METRIC_ICON_PRESETS } from "./OverviewMetricIcon"

const formatPercent = value => `${value.toFixed(2).replace(".", ",")}%`

const formatAmountWithCommaDecimals = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return "0,00"
  return n.toFixed(2).replace(".", ",")
}

function ExpensesMetricCard({
  label,
  helper,
  infoText,
  loading,
  valueNode,
  iconPreset,
}) {
  return (
    <OverviewCard
      iconPreset={iconPreset}
      title={label}
      infoText={infoText}
      loading={loading}
      sx={{ height: "100%" }}
    >
      {valueNode}
      {helper ? (
        <Typography variant="body2" sx={overviewCardHelperTextSx}>
          {helper}
        </Typography>
      ) : null}
    </OverviewCard>
  )
}

ExpensesMetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  helper: PropTypes.string,
  infoText: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  valueNode: PropTypes.node,
  iconPreset: PropTypes.oneOf(Object.keys(OVERVIEW_METRIC_ICON_PRESETS)),
}

ExpensesMetricCard.defaultProps = {
  helper: undefined,
  loading: false,
  valueNode: null,
}

const ExpensesCard = ({ data, currency }) => {
  const loading = !data

  const imputedPercent =
    data && data.expenseTotal !== 0
      ? (data.expenseImputationTotal / data.expenseTotal) * 100
      : 0

  const totalWithTaxesHelper =
    !loading && data
      ? i18n.t("overview.expensesMetricTotalWithTaxes", {
          value: `${formatAmountWithCommaDecimals(
            data.expenseTotalWithTaxes,
          )}${currency || ""}`,
        })
      : undefined

  const toImputeHelper =
    !loading && data
      ? i18n.t(
          (data.expensesPendingImputationCount ?? 0) === 1
            ? "overview.expensesMetricToImputeHelperCount_one"
            : "overview.expensesMetricToImputeHelperCount_other",
          { count: data.expensesPendingImputationCount ?? 0 },
        )
      : undefined

  const countUpProps = {
    start: 0,
    duration: 2,
    separator: ".",
    decimals: 2,
    decimal: ",",
    suffix: currency || "",
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={overviewCardsGridSx4}>
        <ExpensesMetricCard
          iconPreset="expensesTotal"
          label={i18n.t("overview.expensesTotal")}
          helper={totalWithTaxesHelper}
          infoText={i18n.t("overview.expensesMetricTotalTooltip")}
          loading={loading}
          valueNode={
            loading ? null : (
              <Typography sx={overviewCardValueSx}>
                <CountUp {...countUpProps} end={data.expenseTotal} />
              </Typography>
            )
          }
        />
        <ExpensesMetricCard
          iconPreset="expensesImputed"
          label={i18n.t("overview.expensesImputed")}
          helper={i18n.t("overview.expensesMetricImputedHelper")}
          infoText={i18n.t("overview.expensesMetricImputedTooltip")}
          loading={loading}
          valueNode={
            loading ? null : (
              <Typography sx={overviewCardValueSx}>
                <CountUp {...countUpProps} end={data.expenseImputationTotal} />
              </Typography>
            )
          }
        />
        <ExpensesMetricCard
          iconPreset="expensesToImpute"
          label={i18n.t("overview.expensesToImpute")}
          helper={toImputeHelper}
          infoText={i18n.t("overview.expensesMetricToImputeTooltip")}
          loading={loading}
          valueNode={
            loading ? null : (
              <Typography sx={overviewCardValueSx}>
                <CountUp {...countUpProps} end={data.expenseToImpute} />
              </Typography>
            )
          }
        />
        <ExpensesMetricCard
          iconPreset="expensesPercentImputed"
          label={i18n.t("overview.expensesPercentImputed")}
          helper={i18n.t("overview.expensesMetricPercentHelper")}
          infoText={i18n.t("overview.expensesMetricPercentTooltip")}
          loading={loading}
          valueNode={
            loading ? null : (
              <Typography sx={overviewCardValueSx}>
                {formatPercent(imputedPercent)}
              </Typography>
            )
          }
        />
      </Box>
    </Box>
  )
}

export default ExpensesCard

ExpensesCard.propTypes = {
  data: PropTypes.shape({
    expenseTotal: PropTypes.number,
    expenseTotalWithTaxes: PropTypes.number,
    expenseImputationTotal: PropTypes.number,
    expenseToImpute: PropTypes.number,
    expensesPendingImputationCount: PropTypes.number,
  }),
  currency: PropTypes.string,
}
